/**
 * Pagewell — private, in-browser PDF page organiser.
 *
 * Bootstraps the UI, owns the interaction wiring, and orchestrates the store
 * (state), thumbs (pdf.js previews) and the assemble worker (pdf-lib output).
 * No file byte ever leaves this tab.
 */

import { emit, mountEventDrawer } from './eventlog';
import { initGlossary } from './glossary';
import { openPdf, type PdfHandle } from './thumbs';
import { OrganizerStore } from './store';
import type { AssembleRequest, AssembleResponse, SourceDoc } from './types';
import {
  baseName,
  formatBytes,
  h,
  icon,
  initModalTriggers,
  outputName,
  toast,
} from './ui';

const THUMB_W = 150; // css px width of a thumbnail card frame
const FRAME_W = 150;
const FRAME_H = 196;
const SRC_COLORS = ['#4f46e5', '#0d9488', '#b45309', '#be123c', '#7c3aed', '#0369a1', '#65a30d', '#c026d3'];

const store = new OrganizerStore();
const thumbCache = new Map<string, HTMLCanvasElement>();
const openHandles: PdfHandle[] = [];
let docColorSeq = 0;
let dragId: string | null = null;
let busy = false;
let lastExport: { blob: Blob; name: string } | null = null;

const app = document.getElementById('app')!;

// ── boot ────────────────────────────────────────────────────────────────────

function boot(): void {
  initModalTriggers();
  initGlossary();
  mountEventDrawer(document.getElementById('event-drawer')!);
  initTheme();
  wireChrome();
  wireGlobalFileDrop();
  wireKeyboard();
  store.subscribe(() => {
    render();
    updateStatus();
  });
  render();
  updateStatus();
  emit('system', 'ok', 'Pagewell ready — everything runs locally in this tab.');
}

// ── header chrome (theme, drawer) ────────────────────────────────────────────

function initTheme(): void {
  const saved = localStorage.getItem('pagewell-theme');
  if (saved === 'dark' || saved === 'light') {
    document.documentElement.setAttribute('data-theme', saved);
  }
}

function wireChrome(): void {
  document.getElementById('theme-toggle')?.addEventListener('click', () => {
    const cur = document.documentElement.getAttribute('data-theme');
    const isDark = cur
      ? cur === 'dark'
      : window.matchMedia('(prefers-color-scheme: dark)').matches;
    const next = isDark ? 'light' : 'dark';
    document.documentElement.setAttribute('data-theme', next);
    localStorage.setItem('pagewell-theme', next);
  });
  document.getElementById('drawer-toggle')?.addEventListener('click', () => {
    const drawer = document.getElementById('event-drawer');
    drawer?.classList.toggle('collapsed');
    document.body.classList.toggle('drawer-open', !drawer?.classList.contains('collapsed'));
  });
}

// ── import ───────────────────────────────────────────────────────────────────

async function importFiles(files: FileList | File[]): Promise<void> {
  const list = Array.from(files).filter((f) => /\.pdf$/i.test(f.name) || f.type === 'application/pdf');
  const rejected = Array.from(files).length - list.length;
  if (rejected > 0) toast(`Skipped ${rejected} non-PDF file${rejected > 1 ? 's' : ''}.`, 'err');
  if (list.length === 0) {
    if (rejected > 0) emit('import', 'warn', 'Ignored non-PDF input.');
    return;
  }
  for (const file of list) {
    await importOne(file);
  }
}

async function importOne(file: File): Promise<void> {
  emit('import', 'info', `Reading ${file.name}`, { size: formatBytes(file.size) });
  setBusy(true, 'reading');
  try {
    const bytes = await file.arrayBuffer();
    let handle: PdfHandle;
    try {
      handle = await openPdf(bytes);
    } catch (err) {
      const msg = err instanceof Error && /password/i.test(err.message)
        ? 'That PDF is password-protected — unlock it first.'
        : 'That file could not be read as a PDF.';
      toast(msg, 'err');
      emit('import', 'err', `${file.name}: ${msg}`);
      return;
    }
    const doc: SourceDoc = {
      id: `d${++docColorSeq}-${baseName(file.name).slice(0, 24)}`,
      name: file.name,
      bytes,
      pageCount: handle.numPages,
      colorIndex: (docColorSeq - 1) % SRC_COLORS.length,
    };
    openHandles.push(handle);
    store.addDoc(doc); // triggers render with skeletons
    emit('import', 'ok', `Added ${file.name}`, { pages: handle.numPages });
    void renderThumbs(doc.id, handle);
  } catch (err) {
    toast('Something went wrong reading that file.', 'err');
    emit('import', 'err', String(err instanceof Error ? err.message : err));
  } finally {
    setBusy(false);
  }
}

/** Render every page of a freshly-added doc into the thumbnail cache. */
async function renderThumbs(docId: string, handle: PdfHandle): Promise<void> {
  const pageItems = store.pages.filter((p) => p.docId === docId);
  let done = 0;
  for (const item of pageItems) {
    try {
      const canvas = await thumbCache.get(item.id) ?? (await handle.renderPage(item.pageIndex, THUMB_W));
      thumbCache.set(item.id, canvas);
      injectThumb(item.id);
    } catch {
      emit('render', 'warn', `Could not preview page ${item.pageIndex + 1} of a document (it will still export).`);
    }
    done++;
    setStatusProgress(`rendering ${done}/${pageItems.length}`);
  }
  setStatusProgress('');
  emit('render', 'ok', `Previewed ${pageItems.length} page${pageItems.length > 1 ? 's' : ''}.`);
}

/** Place a cached canvas into its card (if the card is on screen). */
function injectThumb(pageId: string): void {
  const card = app.querySelector<HTMLElement>(`.thumb[data-page-id="${pageId}"]`);
  const canvas = thumbCache.get(pageId);
  if (!card || !canvas) return;
  const frame = card.querySelector('.thumb-frame');
  if (!frame) return;
  frame.classList.remove('loading');
  frame.innerHTML = '';
  frame.appendChild(canvas);
  const item = store.pages.find((p) => p.id === pageId);
  layoutThumb(canvas, item?.rotation ?? 0);
}

/** Size + rotate a canvas so it fits the fixed frame at any rotation. */
function layoutThumb(canvas: HTMLCanvasElement, rotation: number): void {
  const aspect = canvas.width / canvas.height || 0.7727;
  const rotated = rotation % 180 !== 0;
  const availW = rotated ? FRAME_H : FRAME_W;
  const availH = rotated ? FRAME_W : FRAME_H;
  let w = availW;
  let ht = w / aspect;
  if (ht > availH) {
    ht = availH;
    w = ht * aspect;
  }
  canvas.style.width = `${Math.round(w)}px`;
  canvas.style.height = `${Math.round(ht)}px`;
  canvas.style.transform = rotation ? `rotate(${rotation}deg)` : '';
}

// ── render ───────────────────────────────────────────────────────────────────

function render(): void {
  app.innerHTML = '';
  if (store.pageCount === 0) {
    app.appendChild(renderEmpty());
    return;
  }
  app.appendChild(renderToolbar());
  if (lastExport) app.appendChild(renderExportResult());
  app.appendChild(renderGrid());
}

function renderEmpty(): HTMLElement {
  const zone = h('div', { class: 'dropzone', tabindex: '0', role: 'button', 'aria-label': 'Add PDF files' });
  zone.appendChild(icon('upload'));
  zone.appendChild(h('h1', { class: 'dz-title' }, 'Drop PDFs to organise'));
  zone.appendChild(
    h(
      'p',
      { class: 'dz-sub' },
      'Merge files, reorder and rotate pages, delete or split — all in your browser. Nothing is uploaded.',
    ),
  );
  const pick = h('button', { class: 'btn btn-primary', type: 'button' }, icon('plus'), 'Choose PDFs');
  zone.appendChild(pick);
  zone.appendChild(h('p', { class: 'dz-hint' }, 'or drag them anywhere on this page · accepts one or many .pdf files'));

  const input = fileInput();
  zone.appendChild(input);
  const open = () => input.click();
  pick.addEventListener('click', (e) => {
    e.stopPropagation();
    open();
  });
  zone.addEventListener('click', open);
  zone.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      open();
    }
  });
  attachFileDnd(zone);
  return zone;
}

function fileInput(): HTMLInputElement {
  const input = h('input', {
    type: 'file',
    accept: 'application/pdf,.pdf',
    multiple: 'true',
    class: 'visually-hidden',
  }) as HTMLInputElement;
  input.addEventListener('change', () => {
    if (input.files && input.files.length) void importFiles(input.files);
    input.value = '';
  });
  return input;
}

function renderToolbar(): HTMLElement {
  const bar = h('div', { class: 'toolbar', role: 'toolbar', 'aria-label': 'Page actions' });

  const addInput = fileInput();
  const addBtn = h('button', { class: 'btn', type: 'button', title: 'Add more PDFs' }, icon('plus'), 'Add');
  addBtn.addEventListener('click', () => addInput.click());

  const selCount = store.selectedCount;
  const allSelected = selCount > 0 && selCount === store.pageCount;
  const selectBtn = h(
    'button',
    { class: 'btn', type: 'button' },
    icon('check'),
    allSelected ? 'Deselect all' : 'Select all',
  );
  selectBtn.addEventListener('click', () => store.setAllSelected(!allSelected));

  const rotateBtn = h(
    'button',
    { class: 'btn', type: 'button', title: selCount ? 'Rotate selected 90°' : 'Rotate all 90°' },
    icon('rotate'),
    selCount ? `Rotate (${selCount})` : 'Rotate all',
  );
  rotateBtn.addEventListener('click', () => {
    store.rotateAll(90);
    emit('edit', 'info', selCount ? `Rotated ${selCount} page(s)` : 'Rotated all pages');
  });

  const delBtn = h(
    'button',
    { class: 'btn btn-danger', type: 'button', title: 'Delete selected pages (Del)' },
    icon('trash'),
    `Delete${selCount ? ` (${selCount})` : ''}`,
  ) as HTMLButtonElement;
  delBtn.disabled = selCount === 0;
  delBtn.addEventListener('click', () => deleteSelected());

  const undoBtn = h('button', { class: 'btn btn-ghost', type: 'button', title: 'Undo (Cmd/Ctrl-Z)' }, icon('undo')) as HTMLButtonElement;
  undoBtn.disabled = !store.canUndo();
  undoBtn.addEventListener('click', () => store.undo());
  const redoBtn = h('button', { class: 'btn btn-ghost', type: 'button', title: 'Redo (Shift-Cmd/Ctrl-Z)' }, icon('redo')) as HTMLButtonElement;
  redoBtn.disabled = !store.canRedo();
  redoBtn.addEventListener('click', () => store.redo());

  const exportSel = h(
    'button',
    { class: 'btn', type: 'button', title: 'Export only the selected pages as a new PDF' },
    icon('download'),
    `Export selected${selCount ? ` (${selCount})` : ''}`,
  ) as HTMLButtonElement;
  exportSel.disabled = selCount === 0 || busy;
  exportSel.addEventListener('click', () => runExport(true));

  const exportAll = h(
    'button',
    { class: 'btn btn-primary', type: 'button', title: 'Export all pages in order' },
    icon('download'),
    store.docCount > 1 ? 'Export merged' : 'Export PDF',
  ) as HTMLButtonElement;
  exportAll.disabled = busy;
  exportAll.addEventListener('click', () => runExport(false));

  const left = h('div', { class: 'toolbar-group' }, addBtn, selectBtn, rotateBtn, delBtn);
  const mid = h('div', { class: 'toolbar-group' }, undoBtn, redoBtn);
  const right = h('div', { class: 'toolbar-group toolbar-right' }, exportSel, exportAll);
  bar.appendChild(left);
  bar.appendChild(mid);
  bar.appendChild(right);
  return bar;
}

function renderExportResult(): HTMLElement {
  const { blob, name } = lastExport!;
  const panel = h('div', { class: 'export-result', role: 'status' });
  panel.appendChild(icon('check'));
  panel.appendChild(
    h('div', { class: 'er-text' },
      h('strong', {}, 'Your PDF is ready'),
      h('span', { class: 'er-meta' }, `${name} · ${formatBytes(blob.size)} · built locally`),
    ),
  );
  const actions = h('div', { class: 'er-actions' });
  const dl = h('button', { class: 'btn btn-primary', type: 'button' }, icon('download'), 'Download');
  dl.addEventListener('click', () => downloadBlob(blob, name));
  actions.appendChild(dl);
  if ('showSaveFilePicker' in window) {
    const save = h('button', { class: 'btn', type: 'button' }, icon('save'), 'Save As…');
    save.addEventListener('click', () => saveAs(blob, name));
    actions.appendChild(save);
  }
  if (canShareFiles(blob, name)) {
    const share = h('button', { class: 'btn', type: 'button' }, icon('share'), 'Share');
    share.addEventListener('click', () => shareBlob(blob, name));
    actions.appendChild(share);
  }
  panel.appendChild(actions);
  return panel;
}

function renderGrid(): HTMLElement {
  const grid = h('div', { class: 'grid', role: 'list', 'aria-label': 'PDF pages' });
  store.pages.forEach((item, index) => {
    const doc = store.getDoc(item.docId);
    const card = h('div', {
      class: `thumb${item.selected ? ' selected' : ''}`,
      draggable: 'true',
      role: 'listitem',
      'data-page-id': item.id,
      'aria-label': `Page ${index + 1}${doc ? ` from ${doc.name}` : ''}`,
    });

    const num = h('div', { class: 'thumb-index' }, String(index + 1));

    const check = h('input', { type: 'checkbox', class: 'thumb-check', 'aria-label': `Select page ${index + 1}` }) as HTMLInputElement;
    check.checked = item.selected;
    check.addEventListener('change', () => store.toggleSelect(item.id));

    const frame = h('div', { class: 'thumb-frame loading' });
    const cached = thumbCache.get(item.id);
    if (cached) {
      frame.classList.remove('loading');
      frame.appendChild(cached);
    } else {
      frame.appendChild(h('div', { class: 'thumb-skeleton' }));
    }

    const srcBadge = h('div', { class: 'thumb-src', title: doc?.name ?? '' });
    if (doc) {
      srcBadge.style.setProperty('--src-color', SRC_COLORS[doc.colorIndex]);
      srcBadge.appendChild(h('span', { class: 'src-dot' }));
      srcBadge.appendChild(h('span', { class: 'src-name' }, shortName(doc.name)));
    }

    const rotBtn = h('button', { class: 'icon-btn', type: 'button', title: 'Rotate 90°', 'aria-label': 'Rotate page' }, icon('rotate'));
    rotBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      store.rotate(item.id, 90);
    });
    const delBtn = h('button', { class: 'icon-btn danger', type: 'button', title: 'Delete page', 'aria-label': 'Delete page' }, icon('trash'));
    delBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      store.remove([item.id]);
      emit('edit', 'info', `Deleted a page — undo with Cmd/Ctrl-Z`);
    });
    const actions = h('div', { class: 'thumb-actions' }, rotBtn, delBtn);

    card.appendChild(num);
    card.appendChild(check);
    card.appendChild(frame);
    card.appendChild(srcBadge);
    card.appendChild(actions);

    wireCardDnd(card, item.id);
    // Re-apply rotation layout if the canvas is already present.
    if (cached) layoutThumb(cached, item.rotation);
    grid.appendChild(card);
  });
  return grid;
}

// ── drag & drop reorder ──────────────────────────────────────────────────────

function wireCardDnd(card: HTMLElement, id: string): void {
  card.addEventListener('dragstart', (e) => {
    dragId = id;
    card.classList.add('dragging');
    e.dataTransfer?.setData('application/x-pagewell-page', id);
    if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
  });
  card.addEventListener('dragend', () => {
    dragId = null;
    card.classList.remove('dragging');
    app.querySelectorAll('.drop-before').forEach((el) => el.classList.remove('drop-before'));
  });
  card.addEventListener('dragover', (e) => {
    if (!dragId || dragId === id) return;
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
    app.querySelectorAll('.drop-before').forEach((el) => el.classList.remove('drop-before'));
    card.classList.add('drop-before');
  });
  card.addEventListener('drop', (e) => {
    if (!dragId || dragId === id) return;
    e.preventDefault();
    reorderTo(dragId, id);
  });
}

function reorderTo(dragged: string, beforeId: string | null): void {
  const pages = store.pages;
  const from = pages.findIndex((p) => p.id === dragged);
  if (from === -1) return;
  let target: number;
  if (beforeId == null) {
    target = pages.length - 1;
  } else {
    const refIdx = pages.findIndex((p) => p.id === beforeId);
    if (refIdx === -1) return;
    target = from < refIdx ? refIdx - 1 : refIdx;
  }
  store.move(dragged, target);
  emit('edit', 'info', 'Reordered a page');
}

// ── delete selected ──────────────────────────────────────────────────────────

function deleteSelected(): void {
  const ids = store.pages.filter((p) => p.selected).map((p) => p.id);
  if (ids.length === 0) return;
  store.remove(ids);
  emit('edit', 'info', `Deleted ${ids.length} page(s) — undo with Cmd/Ctrl-Z`);
}

// ── export via worker ────────────────────────────────────────────────────────

async function runExport(selectedOnly: boolean): Promise<void> {
  if (busy) return;
  const plan = store.plan(selectedOnly);
  if (plan.length === 0) {
    toast('No pages to export.', 'err');
    return;
  }
  setBusy(true, 'assembling');
  emit('export', 'info', `Assembling ${plan.length} page(s)…`);
  try {
    const bytes = await assembleInWorker(plan);
    const blob = new Blob([bytes], { type: 'application/pdf' });
    const names = store.allDocs().map((d) => d.name);
    const name = outputName(names, selectedOnly);
    lastExport = { blob, name };
    emit('export', 'ok', `Built ${name}`, { size: formatBytes(blob.size), pages: plan.length });
    setBusy(false);
    render();
    updateStatus();
    toast('PDF ready — download, save or share it.', 'ok');
    // Auto-scroll to the result panel.
    app.querySelector('.export-result')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  } catch (err) {
    setBusy(false);
    const msg = err instanceof Error ? err.message : String(err);
    toast(`Export failed: ${msg}`, 'err');
    emit('export', 'err', `Export failed: ${msg}`);
  }
}

function assembleInWorker(plan: AssembleRequest['plan']): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    let worker: Worker;
    try {
      worker = new Worker(new URL('./assemble.worker.ts', import.meta.url), { type: 'module' });
    } catch (err) {
      reject(err);
      return;
    }
    const timeout = window.setTimeout(() => {
      worker.terminate();
      reject(new Error('Assembly timed out.'));
    }, 120_000);
    worker.onmessage = (ev: MessageEvent<AssembleResponse>) => {
      const m = ev.data;
      if (m.type === 'progress') {
        setStatusProgress(`assembling ${m.done}/${m.total}`);
      } else if (m.type === 'done') {
        clearTimeout(timeout);
        setStatusProgress('');
        worker.terminate();
        resolve(m.bytes);
      } else if (m.type === 'error') {
        clearTimeout(timeout);
        setStatusProgress('');
        worker.terminate();
        reject(new Error(m.message));
      }
    };
    worker.onerror = (e) => {
      clearTimeout(timeout);
      worker.terminate();
      reject(new Error(e.message || 'Worker crashed during assembly.'));
    };
    // Structured-clone the source bytes (do NOT transfer — the store keeps them).
    const req: AssembleRequest = { type: 'assemble', sources: store.sourcesFor(plan), plan };
    worker.postMessage(req);
  });
}

// ── output delivery ──────────────────────────────────────────────────────────

function downloadBlob(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const a = h('a', { href: url, download: name }) as HTMLAnchorElement;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
  emit('io', 'ok', `Downloaded ${name}`);
}

async function saveAs(blob: Blob, name: string): Promise<void> {
  try {
    // @ts-expect-error — File System Access API is not in TS DOM lib yet.
    const handle = await window.showSaveFilePicker({
      suggestedName: name,
      types: [{ description: 'PDF document', accept: { 'application/pdf': ['.pdf'] } }],
    });
    const writable = await handle.createWritable();
    await writable.write(blob);
    await writable.close();
    toast('Saved.', 'ok');
    emit('io', 'ok', `Saved ${name} to disk`);
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') return;
    toast('Save cancelled or unsupported — use Download instead.', 'err');
    emit('io', 'warn', 'Save As failed; falling back to download.');
  }
}

function canShareFiles(blob: Blob, name: string): boolean {
  const nav = navigator as Navigator & { canShare?: (d: ShareData) => boolean };
  if (!nav.canShare) return false;
  try {
    return nav.canShare({ files: [new File([blob], name, { type: 'application/pdf' })] });
  } catch {
    return false;
  }
}

async function shareBlob(blob: Blob, name: string): Promise<void> {
  const file = new File([blob], name, { type: 'application/pdf' });
  try {
    await navigator.share({ files: [file], title: name });
    emit('io', 'ok', `Shared ${name}`);
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') return;
    toast('Share failed — try Download.', 'err');
    emit('io', 'warn', 'Share failed.');
  }
}

// ── global file drop + keyboard ──────────────────────────────────────────────

function attachFileDnd(el: HTMLElement): void {
  el.addEventListener('dragover', (e) => {
    if (e.dataTransfer?.types.includes('Files')) {
      e.preventDefault();
      el.classList.add('dz-hot');
    }
  });
  el.addEventListener('dragleave', () => el.classList.remove('dz-hot'));
  el.addEventListener('drop', (e) => {
    if (!e.dataTransfer?.types.includes('Files')) return;
    e.preventDefault();
    el.classList.remove('dz-hot');
    if (e.dataTransfer.files.length) void importFiles(e.dataTransfer.files);
  });
}

function wireGlobalFileDrop(): void {
  const overlay = h('div', { class: 'file-overlay', hidden: 'true' }, h('div', { class: 'file-overlay-inner' }, icon('upload'), h('span', {}, 'Drop PDFs to add them')));
  document.body.appendChild(overlay);
  let depth = 0;
  window.addEventListener('dragenter', (e) => {
    if (!e.dataTransfer?.types.includes('Files')) return;
    depth++;
    overlay.hidden = false;
  });
  window.addEventListener('dragover', (e) => {
    if (e.dataTransfer?.types.includes('Files')) e.preventDefault();
  });
  window.addEventListener('dragleave', () => {
    depth = Math.max(0, depth - 1);
    if (depth === 0) overlay.hidden = true;
  });
  window.addEventListener('drop', (e) => {
    depth = 0;
    overlay.hidden = true;
    if (!e.dataTransfer?.types.includes('Files')) return;
    e.preventDefault();
    if (e.dataTransfer.files.length) void importFiles(e.dataTransfer.files);
  });
}

function wireKeyboard(): void {
  document.addEventListener('keydown', (e) => {
    if (document.body.classList.contains('modal-open')) return;
    const target = e.target;
    const typing =
      target instanceof HTMLElement &&
      target.matches('input, textarea, [contenteditable="true"]');
    const meta = e.metaKey || e.ctrlKey;
    if (meta && e.key.toLowerCase() === 'z') {
      e.preventDefault();
      if (e.shiftKey) store.redo();
      else store.undo();
      return;
    }
    if (meta && e.key.toLowerCase() === 'y') {
      e.preventDefault();
      store.redo();
      return;
    }
    if (!typing && (e.key === 'Delete' || e.key === 'Backspace') && store.selectedCount > 0) {
      e.preventDefault();
      deleteSelected();
    }
  });
}

// ── status bar ───────────────────────────────────────────────────────────────

function updateStatus(): void {
  const docs = document.getElementById('sb-docs');
  const pages = document.getElementById('sb-pages');
  if (docs) docs.textContent = `${store.docCount} file${store.docCount === 1 ? '' : 's'}`;
  if (pages) {
    const sel = store.selectedCount;
    pages.textContent = `${store.pageCount} page${store.pageCount === 1 ? '' : 's'}${sel ? ` · ${sel} selected` : ''}`;
  }
}

function setBusy(on: boolean, label = 'working'): void {
  busy = on;
  const dot = document.getElementById('sb-status-dot');
  const lbl = document.getElementById('sb-status-label');
  if (dot) dot.className = `dot-mini ${on ? 'working' : 'ready'}`;
  if (lbl) lbl.textContent = on ? label : 'ready';
  // The toolbar's export buttons are gated on `busy`; re-render so they reflect
  // the new state. (Guarded on pageCount so the empty-state dropzone doesn't
  // flash during the very first file read, before any page exists.)
  if (store.pageCount > 0) render();
}

function setStatusProgress(text: string): void {
  const el = document.getElementById('sb-progress');
  if (el) el.textContent = text;
}

// ── small helpers ────────────────────────────────────────────────────────────

function shortName(name: string): string {
  const b = name.replace(/\.pdf$/i, '');
  return b.length > 18 ? `${b.slice(0, 16)}…` : b;
}

boot();
